import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, RefreshCw } from 'lucide-react';

const BuildingAreasManagement = () => {
  const [areas, setAreas] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [filterLocation, setFilterLocation] = useState('all');

  const [formData, setFormData] = useState({
    location_id: '',
    location_name: '',
    area_type: 'office_suite',
    area_name: '',
    area_name_es: '',
    floor_number: '',
    is_active: true,
    display_order: 0
  });

  const areaTypes = [
    { value: 'office_suite', label: 'Office Suite' },
    { value: 'classroom', label: 'Classroom' },
    { value: 'special_zone', label: 'Special Zone' },
    { value: 'common_area', label: 'Common Area' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      const [areasResponse, locationsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/building-areas`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/service-locations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const fetchedAreas = Array.isArray(areasResponse.data) ? areasResponse.data : [];
      const fetchedLocations = Array.isArray(locationsResponse.data) ? locationsResponse.data : [];
      
      setAreas(fetchedAreas);
      setLocations(fetchedLocations);
      
      // Reset filter to 'all' on data load to ensure all areas are visible
      setFilterLocation('all');
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load data');
      setAreas([]);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingArea
        ? `${process.env.REACT_APP_BACKEND_URL}/api/building-areas/${editingArea.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/building-areas`;

      const method = editingArea ? 'put' : 'post';

      await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await fetchData();
      closeModal();
    } catch (err) {
      console.error('Failed to save:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to save building area');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this building area?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/building-areas/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      await fetchData();
    } catch (err) {
      console.error('Failed to delete:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to delete building area');
    }
  };

  const openModal = (area = null) => {
    if (area) {
      setEditingArea(area);
      setFormData({
        location_id: area.location_id,
        location_name: area.location_name,
        area_type: area.area_type,
        area_name: area.area_name,
        area_name_es: area.area_name_es || '',
        floor_number: area.floor_number || '',
        is_active: area.is_active,
        display_order: area.display_order
      });
    } else {
      setEditingArea(null);
      setFormData({
        location_id: '',
        location_name: '',
        area_type: 'office_suite',
        area_name: '',
        area_name_es: '',
        floor_number: '',
        is_active: true,
        display_order: 0
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingArea(null);
  };

  const handleLocationChange = (e) => {
    const selectedLocation = locations.find(loc => loc.id === e.target.value);
    setFormData({
      ...formData,
      location_id: e.target.value,
      location_name: selectedLocation ? selectedLocation.location_name : ''
    });
  };

  const filteredAreas = filterLocation === 'all'
    ? areas
    : areas.filter(area => area.location_id === filterLocation);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading building areas...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Building Areas Management</h1>
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add Area
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded">
          {error}
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Filter by Location:</label>
        <select
          value={filterLocation}
          onChange={(e) => setFilterLocation(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded w-64"
        >
          <option value="all">All Locations</option>
          {locations.map(loc => (
            <option key={loc.id} value={loc.id}>
              {loc.location_name}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Area Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Floor</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAreas.map(area => (
              <tr key={area.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{area.area_name}</div>
                  {area.area_name_es && (
                    <div className="text-sm text-gray-500">{area.area_name_es}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {areaTypes.find(t => t.value === area.area_type)?.label || area.area_type}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {area.location_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {area.floor_number || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded ${
                    area.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {area.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex gap-2">
                    <button
                      onClick={() => openModal(area)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(area.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredAreas.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No building areas found. Click "Add Area" to create one.
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingArea ? 'Edit Building Area' : 'Add Building Area'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Location *</label>
                <select
                  value={formData.location_id}
                  onChange={handleLocationChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                >
                  <option value="">Select Location</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.location_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Area Type *</label>
                <select
                  value={formData.area_type}
                  onChange={(e) => setFormData({ ...formData, area_type: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                >
                  {areaTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Area Name (English) *</label>
                <input
                  type="text"
                  value={formData.area_name}
                  onChange={(e) => setFormData({ ...formData, area_name: e.target.value })}
                  required
                  placeholder="e.g., Suite 401, Room 101"
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Area Name (Spanish)</label>
                <input
                  type="text"
                  value={formData.area_name_es}
                  onChange={(e) => setFormData({ ...formData, area_name_es: e.target.value })}
                  placeholder="e.g., Suite 401, Habitación 101"
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Floor Number</label>
                <input
                  type="text"
                  value={formData.floor_number}
                  onChange={(e) => setFormData({ ...formData, floor_number: e.target.value })}
                  placeholder="e.g., 4, Ground, Basement"
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Display Order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm font-medium">Active</label>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingArea ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildingAreasManagement;
